<script setup lang="ts">
import { ref, computed, watch, onMounted, onScopeDispose, nextTick, defineAsyncComponent } from 'vue';
// Async import on purpose — same reason as QuoteWidget.vue: a sync vuepic
// import is dragged onto every page that route-prefetches /prices. Dynamic
// import() keeps the 84 KiB datepicker chunk out of prefetch; it loads when
// this calculator hydrates. Picker is inside <ClientOnly> — no SSR markup.
// CSS stays static to avoid FOUC.
import '@vuepic/vue-datepicker/dist/main.css';
import { toIsoLocal, formatDMY } from '~/utils/iso-date';
import { getMaxBookableDate } from '~~/utils/booking-window';
import { usePricesCurrency } from '~/composables/usePricesCurrency';

const VueDatePicker = defineAsyncComponent(() =>
  import('@vuepic/vue-datepicker').then((m) => m.VueDatePicker),
);

const { t } = useI18n();
const runtimeConfig = useRuntimeConfig();
const colorMode = useColorMode();
const isDark = computed(() => colorMode.value === 'dark');
const route = useRoute();
const router = useRouter();
const { trackEvent } = useAnalytics();
const currency = usePricesCurrency();

const SLIDER_MIN = 1;
const SLIDER_MAX = 30;
const SLIDER_DEFAULT = 7;
const SHORT_STAY_MAX = 2; // 1..2 nights triggers hint

const parseIso = (s?: string | null): Date | null => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const addDays = (d: Date, n: number): Date => {
  const out = new Date(d.getTime());
  out.setDate(out.getDate() + n);
  return out;
};

const diffDays = (a: Date, b: Date): number =>
  Math.round((b.getTime() - a.getTime()) / 86_400_000);

const minDate = ref<Date | null>(null);
const maxDate = ref<Date | null>(null);
const arriveDate = ref<Date | null>(null);
const nights = ref<number>(SLIDER_DEFAULT);
const peopleCount = ref<number>(2);
let hasFiredFirstQuote = false;
let hydrated = false;

const departDate = computed<Date | null>(() =>
  arriveDate.value ? addDays(arriveDate.value, nights.value) : null,
);

const departFormatted = computed(() =>
  departDate.value ? formatDMY(toIsoLocal(departDate.value)) : '',
);

const nightsLabel = computed(() =>
  t('prices.calculator.nightsLabel', {
    n: nights.value,
    count: t('prices.calculator.nights', nights.value, { named: { n: nights.value } }),
  }),
);

const isShortStay = computed(
  () => nights.value >= SLIDER_MIN && nights.value <= SHORT_STAY_MAX,
);

function pushUrl(changed: 'checkin' | 'slider' | 'guests') {
  if (!arriveDate.value) return;
  const from = toIsoLocal(arriveDate.value);
  const to = toIsoLocal(addDays(arriveDate.value, nights.value));
  router.push({
    path: route.path,
    query: { ...route.query, from, to, guests: peopleCount.value },
  });
  if (!hasFiredFirstQuote) {
    hasFiredFirstQuote = true;
    trackEvent('prices_calculator_first_quote', {
      from,
      to,
      guests: peopleCount.value,
      nights: nights.value,
    });
  } else {
    trackEvent('prices_calculator_change', {
      from,
      to,
      guests: peopleCount.value,
      nights: nights.value,
      changed,
    });
  }
}

let sliderTimer: ReturnType<typeof setTimeout> | null = null;
function pushUrlDebouncedForSlider() {
  if (sliderTimer) clearTimeout(sliderTimer);
  sliderTimer = setTimeout(() => {
    sliderTimer = null;
    pushUrl('slider');
  }, 500);
}

watch(arriveDate, () => {
  if (!hydrated) return;
  if (arriveDate.value) pushUrl('checkin');
});
watch(peopleCount, () => {
  if (!hydrated) return;
  if (arriveDate.value) pushUrl('guests');
});
watch(nights, (newN, oldN) => {
  if (!hydrated) return;
  if (arriveDate.value && newN !== oldN) pushUrlDebouncedForSlider();
});

onScopeDispose(() => {
  if (sliderTimer) clearTimeout(sliderTimer);
});

watch(
  () => route.query,
  (q) => {
    const fromQ = typeof q.from === 'string' ? q.from : '';
    const toQ = typeof q.to === 'string' ? q.to : '';
    const guestsQ = Number(q.guests);

    const a = parseIso(fromQ);
    const d = parseIso(toQ);
    if (a && d && a < d) {
      if ((arriveDate.value?.getTime() ?? 0) !== a.getTime()) arriveDate.value = a;
      let n = diffDays(a, d);
      if (n < SLIDER_MIN) n = SLIDER_DEFAULT;
      if (n > SLIDER_MAX) n = SLIDER_MAX;
      if (nights.value !== n) nights.value = n;
    } else if (!fromQ && !toQ) {
      if (arriveDate.value !== null) arriveDate.value = null;
      if (nights.value !== SLIDER_DEFAULT) nights.value = SLIDER_DEFAULT;
      hasFiredFirstQuote = false;
    }

    if (Number.isFinite(guestsQ) && guestsQ > 0 && guestsQ <= 5) {
      const g = Math.trunc(guestsQ);
      if (peopleCount.value !== g) peopleCount.value = g;
    }
  },
);

onMounted(async () => {
  const now = new Date();
  minDate.value = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  maxDate.value = getMaxBookableDate(Number(runtimeConfig.public.bookingWindowDays), now);

  const fromQ = typeof route.query.from === 'string' ? route.query.from : '';
  const toQ = typeof route.query.to === 'string' ? route.query.to : '';
  const guestsQ = Number(route.query.guests);

  // Apply guests from URL first so the default-path below uses the
  // correct count when it replaces the URL.
  if (Number.isFinite(guestsQ) && guestsQ > 0 && guestsQ <= 5) {
    peopleCount.value = Math.trunc(guestsQ);
  }

  const a = parseIso(fromQ);
  const d = parseIso(toQ);
  if (a && d && a < d) {
    arriveDate.value = a;
    let n = diffDays(a, d);
    if (n < SLIDER_MIN) n = SLIDER_DEFAULT;
    if (n > SLIDER_MAX) {
      n = SLIDER_MAX;
      // Rewrite URL to reflect clamp so back-forward works consistently.
      router.replace({
        path: route.path,
        query: { ...route.query, to: toIsoLocal(addDays(a, SLIDER_MAX)) },
      });
    }
    nights.value = n;
    hasFiredFirstQuote = true; // don't double-fire on hydration
  } else {
    // No dates in URL → default to tomorrow + SLIDER_DEFAULT nights.
    // Replace URL so QuoteRows renders and the state is shareable,
    // but without pushing a history entry or firing first_quote.
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    arriveDate.value = tomorrow;
    const fromIso = toIsoLocal(tomorrow);
    const toIso = toIsoLocal(addDays(tomorrow, nights.value));
    await router.replace({
      path: route.path,
      query: { ...route.query, from: fromIso, to: toIso, guests: peopleCount.value },
    });
    hasFiredFirstQuote = true; // auto-default is not a user-driven first quote
  }
  await nextTick();
  hydrated = true;
});

const formatDate = (d: Date | Date[]) => {
  const date = Array.isArray(d) ? d[0] : d;
  return date ? formatDMY(toIsoLocal(date)) : '';
};

async function clear() {
  // Reset to the same defaults applied on first mount: tomorrow,
  // 7 nights, 2 guests. Suppress watchers while we bulk-update state
  // so we don't fire spurious prices_calculator_change events.
  hydrated = false;
  const now = new Date();
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  arriveDate.value = tomorrow;
  nights.value = SLIDER_DEFAULT;
  peopleCount.value = 2;
  const fromIso = toIsoLocal(tomorrow);
  const toIso = toIsoLocal(addDays(tomorrow, SLIDER_DEFAULT));
  await router.push({
    path: route.path,
    query: { ...route.query, from: fromIso, to: toIso, guests: 2 },
  });
  trackEvent('prices_calculator_clear', {});
  await nextTick();
  hydrated = true;
}

const hasResult = computed(() => !!route.query.from && !!route.query.to);
</script>

<template>
  <section class="relative overflow-hidden py-8 md:py-12 bg-white dark:bg-slate-900">
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />
    <div class="relative container">
      <div data-reveal class="text-center max-w-2xl mx-auto mb-6">
        <div class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          <span>{{ t('prices.calculator.eyebrow') }}</span>
          <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
        </div>
        <h2 class="mt-3 font-display italic text-2xl md:text-3xl text-slate-900 dark:text-white">
          {{ t('prices.calculator.title') }}
        </h2>
        <p class="mt-2 font-body text-sm text-slate-600 dark:text-slate-300">
          {{ t('prices.calculator.subtitle') }}
        </p>
      </div>

      <div data-reveal class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 overflow-hidden">
        <div class="p-4 md:p-6">
          <div class="flex flex-wrap items-end justify-end gap-3 mb-4">
            <div
              class="inline-flex rounded-full border border-slate-300/70 dark:border-slate-700 bg-white/60 dark:bg-slate-950/30 p-1"
              role="group"
              :aria-label="t('prices.calculator.currencyLabel')"
            >
              <button
                v-for="c in (['USD', 'GEL', 'RUB'] as const)"
                :key="c"
                type="button"
                :class="[
                  'px-4 py-1 text-xs font-body rounded-full transition duration-300',
                  currency === c
                    ? 'bg-slate-900 text-amber-200 dark:bg-amber-400 dark:text-slate-900'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
                ]"
                @click="currency = c"
              >
                {{ c }}
              </button>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)_auto] gap-4 md:gap-6 items-start">
            <!-- CHECK-IN -->
            <div>
              <label
                class="block font-body text-xs uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2"
                for="prices-calc-arrive"
              >
                {{ t('prices.calculator.checkin') }}
              </label>
              <ClientOnly>
                <div class="prices-calc-datepicker">
                  <VueDatePicker
                    id="prices-calc-arrive"
                    v-model="arriveDate"
                    :min-date="minDate ?? undefined"
                    :max-date="maxDate ?? undefined"
                    :time-config="{ enableTimePicker: false }"
                    :formats="{ input: formatDate }"
                    :dark="isDark"
                    teleport="body"
                    auto-apply
                    clearable
                    :placeholder="t('prices.calculator.checkin')"
                  />
                </div>
              </ClientOnly>
            </div>

            <!-- SLIDER -->
            <div>
              <div class="flex items-baseline justify-between gap-3 mb-1">
                <label
                  class="font-body text-xs uppercase tracking-wider text-slate-600 dark:text-slate-300"
                  for="prices-calc-nights"
                >
                  {{ t('prices.calculator.sliderLabel') }}
                </label>
                <span
                  v-if="isShortStay"
                  class="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-500/20 px-2 py-0.5 text-[10px] font-body font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300"
                >
                  {{ t('prices.calculator.shortStayHint') }}
                </span>
              </div>
              <div class="mb-2 text-center font-display italic text-xl md:text-2xl tabular-nums text-slate-900 dark:text-white leading-none">
                {{ t('prices.calculator.nights', nights, { named: { n: nights } }) }}
              </div>
              <input
                id="prices-calc-nights"
                v-model.number="nights"
                type="range"
                :min="SLIDER_MIN"
                :max="SLIDER_MAX"
                step="1"
                :aria-label="t('prices.calculator.sliderLabel')"
                :aria-valuemin="SLIDER_MIN"
                :aria-valuemax="SLIDER_MAX"
                :aria-valuenow="nights"
                :aria-valuetext="nightsLabel"
                class="w-full accent-amber-400 h-2 cursor-pointer"
              >
              <div class="flex justify-between items-baseline text-xs mt-1 gap-2">
                <span class="text-slate-400 dark:text-slate-500 tabular-nums">{{ SLIDER_MIN }}</span>
                <span
                  v-if="departFormatted"
                  class="font-body font-semibold text-red-500 dark:text-red-400 tabular-nums truncate"
                >
                  {{ t('prices.calculator.departureHint', { date: departFormatted }) }}
                </span>
                <span class="text-slate-400 dark:text-slate-500 tabular-nums">{{ SLIDER_MAX }}</span>
              </div>
            </div>

            <!-- GUESTS + CLEAR -->
            <div>
              <label
                class="block font-body text-xs uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2"
                for="prices-calc-guests"
              >
                {{ t('prices.calculator.guests') }}
              </label>
              <div class="flex gap-2 items-stretch justify-between md:justify-start">
                <div class="relative w-16 md:mr-2">
                  <select
                    id="prices-calc-guests"
                    v-model.number="peopleCount"
                    class="w-full h-12 pl-3 pr-7 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white font-body tabular-nums appearance-none cursor-pointer"
                  >
                    <option v-for="n in 5" :key="n" :value="n">{{ n }}</option>
                  </select>
                  <Icon
                    name="lucide:chevron-down"
                    class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 size-4 text-slate-500 dark:text-slate-400"
                    aria-hidden="true"
                  />
                </div>
                <button
                  v-if="hasResult"
                  type="button"
                  class="h-12 px-4 rounded-full bg-red-500 hover:bg-red-600 text-white font-body font-semibold text-sm transition"
                  @click="clear"
                >
                  {{ t('prices.calculator.clear') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <slot name="results" />
      </div>
    </div>
  </section>
</template>

<style>
/* Global (not scoped): VueDatePicker teleports parts of its UI. */
.prices-calc-datepicker .dp__input {
  height: 3rem;
  padding-left: 2.75rem;
  padding-right: 0.75rem;
  border-radius: 0.5rem;
  /* 16px — prevents iOS Safari zoom-on-focus, which kicks in below 16px. */
  font-size: 1rem;
  background-color: #ffffff;
  border: 1px solid rgb(203 213 225); /* slate-300 */
  color: rgb(15 44 62); /* slate-900 */
  transition: border-color 0.2s, background-color 0.2s;
}
.prices-calc-datepicker .dp__input::placeholder {
  color: rgb(100 116 139); /* slate-500 */
}
.prices-calc-datepicker .dp__input:hover,
.prices-calc-datepicker .dp__input:focus {
  border-color: rgb(148 163 184); /* slate-400 */
}
.prices-calc-datepicker .dp__input_icon {
  color: rgb(100 116 139); /* slate-500 */
  left: 1rem;
}

html.dark .prices-calc-datepicker .dp__input {
  background-color: rgb(15 44 62); /* slate-900 */
  border-color: rgb(29 75 102); /* slate-700 */
  color: #ffffff;
}
html.dark .prices-calc-datepicker .dp__input::placeholder {
  color: rgb(148 163 184); /* slate-400 */
}
html.dark .prices-calc-datepicker .dp__input:hover,
html.dark .prices-calc-datepicker .dp__input:focus {
  border-color: rgb(100 116 139); /* slate-500 */
}
html.dark .prices-calc-datepicker .dp__input_icon {
  color: rgb(148 163 184); /* slate-400 */
}
</style>
